import { useLocation } from "@reach/router"
import { Link, graphql, useStaticQuery } from "gatsby"
import { FormattedMessage, useIntl } from "gatsby-plugin-react-intl"
import React from "react"
import styled, { css } from "styled-components"

// import components
import { Col, Container, Row } from "../components/Grid"
import Markdown from "../components/Markdown"
import Snippet from "../components/Snippet"

import LogoAxelSpringer from "../images/partners/logorow-axel_springer.inline.svg"
import LogoBoerseStuttgart from "../images/partners/logorow-boerse_stuttgart.inline.svg"
import LogoFinanzenNet from "../images/partners/logorow-finanzen_net.inline.svg"
import LogoSBIHoldings from "../images/partners/logorow-sbi_holdings.inline.svg"
import LogoSolaris from "../images/partners/logorow-solaris.inline.svg"

import InstagramSVG from "../images/icons/instagram.inline.svg"
import LinkedinSVG from "../images/icons/linkedin.inline.svg"
import TwitterSVG from "../images/icons/twitter.inline.svg"

import { media } from "../styles"

const FooterContainer = styled.footer`
  background: ${({ theme }) =>
    theme.name === "dark" ? "transparent" : theme.brandSecondaryLighter};
  border-top: ${({ theme }) =>
    theme.name === "dark" ? "1px solid rgba(255,255,255,0.2)" : ""};
  color: ${({ theme }) => theme.textColorLight};
  font-size: 10px;
  padding: 30px 0;

  @media ${media.sm} {
    font-size: 12px;
    padding: 45px 0 45px;
  }
`

const FooterNavContainer = styled.div`
  margin-bottom: 20px;

  @media ${media.sm} {
    margin-bottom: 30px;
  }
`

const FooterNav = styled.ul`
  display: flex;
  flex-direction: column;
  list-style: none;
  line-height: 1.2;
  margin: 0;
  padding: 0;
`

const FooterNavHeader = styled.h3`
  font-size: 12px;
  font-weight: ${({ theme }) => theme.fontWeightBold};
  color: inherit;
  line-height: 1.2;
  margin-bottom: 0.75rem;

  @media ${media.sm} {
    font-size: 16px;
    margin-bottom: 1rem;
  }
`

const FooterNavItem = styled.li`
  font-size: inherit;
  line-height: inherit;
  margin: 0 0 0.75rem;
  order: 1;

  a {
    color: ${({ theme }) => theme.textColorLight};

    &:hover {
      color: ${({ theme }) =>
        theme.name === "dark"
          ? theme.brandSecondaryLight
          : theme.brandSecondaryDark};
    }

    &.active {
      color: ${({ theme }) =>
        theme.name === "dark"
          ? theme.brandSecondaryLight
          : theme.brandSecondaryDark};
    }
  }
`

const PoweredBy = styled.div`
  margin-bottom: 20px;

  @media ${media.md} {
    margin-bottom: 0;
  }

  strong {
    display: block;
    font-weight: ${({ theme }) => theme.fontWeightNormal};
    margin: 0 0 1rem;
    line-height: 1.2;
  }
`

const Logos = styled.ul`
  display: flex;
  flex-flow: row wrap;
  list-style: none;
  justify-content: flex-start;
  margin: 0 -15px;
  padding: 0;

  @media ${media.md} {
    flex-flow: row wrap;
    margin: 0 -10px;
  }

  @media ${media.xl} {
    padding-right: 10px;
  }
`

const LogosItem = styled.li`
  align-items: center;
  display: flex;
  flex: none;
  margin: 0 0 10px;
  padding: 0 15px;
  width: 33.3333333%;

  @media ${media.md} {
    padding: 0 10px;
    width: 50%;
  }

  @media ${media.lg} {
    width: 33.3333333%;
  }

  svg {
    display: block;
    height: auto;
    max-width: 100%;
  }

  a {
    opacity: 0.4;
    transition: opacity ${({ theme }) => theme.transitionTime};

    &,
    &:hover {
      color: ${({ theme }) => (theme.name === "dark" ? "white" : "black")};
    }

    &:hover {
      opacity: 0.5;
    }
  }

  ${(props) =>
    props.small &&
    css`
      img {
        max-width: 90%;

        @media ${media.lg} {
          max-width: 170px;
        }
      }
    `}
`

const FooterCopyright = styled(FooterNavItem).attrs({
  as: "div",
})`
  align-items: center;
  display: flex;
  flex-direction: column;
  margin-top: 30px;

  @media ${media.sm} {
    margin-top: 0;
    order: 0;
  }

  @media ${media.md} {
    flex-direction: row;
    margin: 3px 0 0 0;
  }
`

const FooterLegal = styled(FooterNavItem).attrs({
  as: "div",
})`
  line-height: 2;
  margin: 15px 0 40px;
`

const FooterBuild = styled.div`
  margin-top: 20px;
  opacity: 0.3;

  @media ${media.md} {
    margin: 0 0 0 auto;
  }
`

const FooterSocial = styled.ul`
  align-items: center;
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  list-style: none;
  margin: 0 auto;
  padding: 0;
  gap: 40px;

  li {
    margin: 0;
  }

  svg,
  img {
    display: block;
  }

  @media ${media.md} {
    margin: 0 0 auto;
    justify-content: flex-end;
  }

  @media ${media.lg} {
    /* margin: 0 0 0 auto; */
    width: auto;
  }

  @media ${media.xl} {
  }
`

const FooterSocialItem = styled.li``

const FooterSocialLink = styled.a`
  color: ${({ theme }) => theme.textColorLight};
  transition: color ${({ theme }) => theme.transitionTime};

  &:hover {
    color: ${({ theme }) =>
      theme.name === "dark"
        ? theme.brandSecondaryLight
        : theme.brandSecondaryDark};
  }

  &.active {
    color: ${({ theme }) =>
      theme.name === "dark"
        ? theme.brandSecondaryLight
        : theme.brandSecondaryDark};
  }
`

const SocialIcon = styled.div`
  ${(props) =>
    props.title === "LinkedIn" &&
    css`
      margin-top: -2px;
    `}
`

const SocialIcons = {
  twitter: TwitterSVG,
  instagram: InstagramSVG,
  linkedin: LinkedinSVG,
}

const Footer = (props) => {
  const { locale, formatMessage } = useIntl()
  const location = useLocation()
  const data = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          company
          socialProfiles {
            name
            url
            logoId
            includeInFooter
          }
        }
      }
    }
  `)

  return (
    <FooterContainer style={{ paddingBottom: `${props.offset + 45}px` }}>
      <Container>
        <Row>
          <Col md={6} lg={7} xl={6}>
            <PoweredBy>
              <strong>
                <FormattedMessage id="poweredBy.header" />
              </strong>
              <Logos>
                <LogosItem>
                  <a
                    href="https://www.boerse-stuttgart.de"
                    target="_blank"
                    rel="noopener"
                  >
                    <LogoBoerseStuttgart alt="Börse Stuttgart" />
                  </a>
                </LogosItem>
                <LogosItem>
                  <a
                    href="https://www.axelspringer.com"
                    target="_blank"
                    rel="noopener"
                  >
                    <LogoAxelSpringer alt="Axel Springer" />
                  </a>
                </LogosItem>
                <LogosItem>
                  <a
                    href="https://www.finanzen.net"
                    target="_blank"
                    rel="noopener"
                  >
                    <LogoFinanzenNet alt="Finanzen.Net" />
                  </a>
                </LogosItem>
                <LogosItem>
                  <a
                    href="https://www.sbigroup.co.jp/english/"
                    target="_blank"
                    rel="noopener"
                  >
                    <LogoSBIHoldings alt="SBI Holdings" />
                  </a>
                </LogosItem>
                <LogosItem>
                  <a
                    href="https://www.solarisgroup.com"
                    target="_blank"
                    rel="noopener"
                  >
                    <LogoSolaris alt="Solaris" />
                  </a>
                </LogosItem>
              </Logos>
            </PoweredBy>
          </Col>
          <Col md={6} lg={4} lgOffset={1} xl={6} xlOffset={0}>
            <Row>
              <Col col={6} md={6} xl={4}>
                <FooterNavContainer>
                  <FooterNavHeader>
                    <FormattedMessage id="footer.company" />
                  </FooterNavHeader>
                  <FooterNav role="navigation">
                    <FooterNavItem>
                      <Link to={`/${locale}/press/`} activeClassName="active">
                        <FormattedMessage id="footer.press" />
                      </Link>
                    </FooterNavItem>
                    <FooterNavItem>
                      <a href={`mailto:support@bsdex.de`}>
                        <FormattedMessage id="footer.contact" />
                      </a>
                    </FooterNavItem>
                    <FooterNavItem>
                      <a
                        href={`mailto:marketing@bsdex.de?subject=${encodeURIComponent(
                          formatMessage({
                            id: "footer.cooperationEmailSubject",
                          })
                        )}`}
                      >
                        <FormattedMessage id="footer.cooperation" />
                      </a>
                    </FooterNavItem>
                    <FooterNavItem>
                      <Link to={`/${locale}/faq/`} activeClassName="active">
                        <FormattedMessage id="footer.faq" />
                      </Link>
                    </FooterNavItem>
                    <FooterNavItem>
                      <Link to={`/${locale}/imprint/`} activeClassName="active">
                        <FormattedMessage id="footer.imprint" />
                      </Link>
                    </FooterNavItem>
                  </FooterNav>
                </FooterNavContainer>
              </Col>
              <Col col={6} md={6} xl={4}>
                <FooterNavContainer>
                  <FooterNavHeader>
                    <FormattedMessage id="footer.legal" />
                  </FooterNavHeader>
                  <FooterNav role="navigation">
                    {location.pathname.indexOf("/institutions/") === -1 && (
                      <FooterNavItem>
                        <Link to={`/${locale}/terms/`} activeClassName="active">
                          <FormattedMessage id="footer.terms" />
                        </Link>
                      </FooterNavItem>
                    )}
                    <FooterNavItem>
                      <Link to={`/${locale}/risk/`} activeClassName="active">
                        <FormattedMessage id="footer.risk" />
                      </Link>
                    </FooterNavItem>
                    <FooterNavItem>
                      <Link
                        to={`/${locale}/privacy-policy/`}
                        activeClassName="active"
                      >
                        <FormattedMessage id="footer.privacy" />
                      </Link>
                    </FooterNavItem>
                    <FooterNavItem>
                      <a href="javascript:showConsentManager();">
                        <FormattedMessage id="footer.adjustCookie" />
                      </a>
                    </FooterNavItem>
                    <FooterNavItem>
                      <Link
                        to={`/${locale}/disclaimer/`}
                        activeClassName="active"
                      >
                        <FormattedMessage id="footer.disclaimer" />
                      </Link>
                    </FooterNavItem>
                    <FooterNavItem>
                      <Link
                        to={`/${locale}/dispute-resolution/`}
                        activeClassName="active"
                      >
                        <FormattedMessage id="footer.disputeResolution" />
                      </Link>
                    </FooterNavItem>
                    <FooterNavItem>
                      <Link
                        to={`/${locale}/beschwerdemanagement/`}
                        activeClassName="active"
                      >
                        <FormattedMessage id="footer.complaintManagement" />
                      </Link>
                    </FooterNavItem>
                  </FooterNav>
                </FooterNavContainer>
              </Col>
              <Col col={6} md={6} xl={4}>
                <FooterNavContainer>
                  <FooterNavHeader>
                    <FormattedMessage id="footer.resources" />
                  </FooterNavHeader>
                  <FooterNav role="navigation">
                    <FooterNavItem>
                      <a
                        href={`https://academy.bsdex.de/krypto-blockchain-glossar`}
                      >
                        <FormattedMessage id="footer.glossary" />
                      </a>
                    </FooterNavItem>
                    <FooterNavItem>
                      <Link
                        to={`/${locale}/security/`}
                        activeClassName="active"
                      >
                        <FormattedMessage id="footer.security" />
                      </Link>
                    </FooterNavItem>
                    <FooterNavItem>
                      <a
                        href={`https://status.bsdex.de`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FormattedMessage id="footer.status" />
                      </a>
                    </FooterNavItem>
                    <FooterNavItem>
                      <a
                        href={`https://www.bsdigital.com/${locale}/nachhaltigkeitsindikatoren/`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FormattedMessage id="footer.cryptoAssetsCarbonFootprint" />
                      </a>
                    </FooterNavItem>
                    <FooterNavItem>
                      <a
                        href={`https://www.bsdex.de/${locale}/micar-delayed-data`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FormattedMessage id="footer.micarDelayedData" />
                      </a>
                    </FooterNavItem>
                  </FooterNav>
                </FooterNavContainer>
              </Col>
            </Row>
          </Col>
        </Row>
        <FooterLegal className="text-small">
          <Markdown>
            {Snippet({ id: "7bWNLkGRvUjpEFCFs4opDB", field: "text" })}
          </Markdown>
        </FooterLegal>
        <Row>
          <Col md="6">
            <FooterCopyright>
              <div>
                &copy; {new Date().getFullYear()}{" "}
                {data.site.siteMetadata.company}
              </div>
              <FooterBuild>{process.env.GATSBY_BUILD_INFO}</FooterBuild>
            </FooterCopyright>
          </Col>
          <Col md="6">
            <FooterSocial>
              {data.site.siteMetadata.socialProfiles.map(
                (profile) =>
                  profile.includeInFooter && (
                    <FooterSocialItem key={profile.name}>
                      <FooterSocialLink
                        href={profile.url}
                        title={profile.name}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <SocialIcon
                          as={SocialIcons[profile.logoId]}
                          title={profile.name}
                        />
                      </FooterSocialLink>
                    </FooterSocialItem>
                  )
              )}
            </FooterSocial>
          </Col>
        </Row>
      </Container>
    </FooterContainer>
  )
}

export default Footer
